import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PublicProfileView } from "@/components/public-profile-view";
import { useT } from "@/i18n";
import { useReferralCapture } from "@/hooks/useReferral";
import { getPublicProfilePage } from "@/lib/profile.functions";
import { profileHead } from "@/lib/profile-seo";

export const Route = createFileRoute("/pro/$slug")({
  validateSearch: (search: Record<string, unknown>) => ({
    ref: typeof search["ref"] === "string" ? (search["ref"] as string) : undefined,
  }),
  loader: async ({ params }) => {
    try {
      return await getPublicProfilePage({ data: { slug: params.slug } });
    } catch {
      return {
        profile: null,
        socials: [],
        services: [],
        posts: [],
        offers: [],
        sections: [],
        articles: [],
        listings: [],
      } as Awaited<ReturnType<typeof getPublicProfilePage>>;
    }
  },
  head: ({ params, loaderData }) => profileHead("pro", params.slug, loaderData),
  component: ProProfileRoute,
});

function ProProfileRoute() {
  const { t } = useT();
  const { slug } = Route.useParams();
  const data = Route.useLoaderData();
  useReferralCapture(data?.profile?.referral_code ?? null);

  if (!data?.profile || !data.profile.pro_enabled) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20">
        <h1 className="font-display text-3xl font-bold">{t("profiles.professionalPageNotFound")}</h1>
        <p className="mt-2 text-muted-foreground">
          {t("profiles.professionalPageNotPublic")}
        </p>
        <Button asChild variant="hot" className="mt-6">
          <Link to="/marketplace">{t("profiles.browseMarketplace")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <PublicProfileView
      variant="pro"
      path={`/pro/${slug}`}
      data={{
        profile: data.profile,
        socials: data.socials,
        services: data.services,
        posts: data.posts,
        offers: data.offers,
        sections: data.sections,
        articles: data.articles,
        listings: data.listings,
      }}
    />
  );
}
